import { forwardRef, useEffect, useState } from 'react';
import styled from 'styled-components';
import { getColorWithAlpha } from '@elfsight/widget-utils';
import { useSettings } from '@wu/widget-core';
import { Direction, Range } from 'react-range';
import type { BackgroundType, ColorScheme } from '../../../../../../types';
import {
  BLACK,
  getAnimation,
  invertBackground,
  WHITE
} from '../../../../../helpers';
import { useTheme } from '../../../../common';
import { usePlayer } from '../../../PlayerProvider';
import { useVolume } from './VolumeProvider';

const getInnerBackground = (
  backgroundType: BackgroundType,
  backgroundColor: string,
  colorScheme: ColorScheme,
  color: string
) => {
  switch (colorScheme) {
    case 'light':
      return WHITE;
    case 'dark':
      return BLACK;
    default:
      return backgroundType === 'color'
        ? backgroundColor
        : invertBackground(color);
  }
};

export const VolumeControlSlider = forwardRef<HTMLDivElement, unknown>(
  (_, ref) => {
    const { sliderPosition } = useVolume();
    const { backgroundType, backgroundColor, colorScheme } = useSettings();
    const { setVolume, volume = 0 } = usePlayer();
    const [seekerPosition, setSeekerPosition] = useState(volume);
    const [isChanging, setIsChanging] = useState(false);
    const { textColor } = useTheme();

    useEffect(() => {
      setSeekerPosition(volume);
    }, [volume]);

    return (
      <VolumeControlSliderComponent
        ref={ref}
        onDoubleClick={() => {
          setVolume(0.5);
          setSeekerPosition(0.5);
        }}
        style={{ opacity: isChanging ? 1 : 0 }}
        _sliderPosition={sliderPosition}
      >
        <Inner
          _background={getInnerBackground(
            backgroundType,
            backgroundColor,
            colorScheme,
            textColor
          )}
        >
          <Range
            step={0.001}
            min={0}
            max={1}
            values={[seekerPosition]}
            onChange={(values) => {
              setSeekerPosition(values[0]);
              setVolume(values[0]);
              setIsChanging(true);
            }}
            onFinalChange={() => setIsChanging(false)}
            direction={Direction.Up}
            renderTrack={({ props, children }) => (
              <Track
                {...props}
                style={{ ...props.style }}
                _background={textColor}
              >
                {children}

                <VolumeLevel
                  style={{ height: `${seekerPosition * 100}%` }}
                  _background={textColor}
                />
              </Track>
            )}
            renderThumb={({ props: { key, ...props } }) => (
              <Thumb key={key} {...props} _background={textColor} />
            )}
          />
        </Inner>
      </VolumeControlSliderComponent>
    );
  }
);

export const VolumeControlSliderComponent = styled.div<{
  _sliderPosition: 'top' | 'bottom';
}>`
  opacity: 0;
  pointer-events: none;
  transition: ${getAnimation()};
  z-index: 999999;
  position: absolute;
  right: 50%;
  transform: translate(50%);
  top: ${({ _sliderPosition }) =>
    _sliderPosition === 'bottom' ? '100%' : 'auto'};
  bottom: ${({ _sliderPosition }) =>
    _sliderPosition === 'bottom' ? 'auto' : '100%'};
`;

const Inner = styled.div<{
  _background: string;
}>`
  width: 24px;
  height: 120px;
  right: 0;
  top: 100%;
  box-shadow: 0 5px 8px 0 rgba(17, 17, 17, 0.2);
  border-radius: 4px;
  margin: 12px 0;
  padding: 12px 0;
  background-color: ${({ _background }) => _background};
`;

const VolumeLevel = styled.div<{
  _background: string;
}>`
  display: block !important;
  position: absolute;
  left: 0;
  z-index: 3;
  pointer-events: none;
  width: 100%;
  bottom: 0;
  border-radius: 0 0 4px 4px;
  background-color: ${({ _background }) => _background};
`;

const Track = styled.div<{
  _background: string;
}>`
  height: 100%;
  width: 4px;
  position: relative;
  margin: 0 auto;
  border-radius: 4px;
  background-color: ${({ _background }) => getColorWithAlpha(_background, 0.3)};
`;

const Thumb = styled.div<{
  _background: string;
}>`
  display: block !important;
  height: 18px;
  width: 18px;
  z-index: 4;
  border-radius: 50%;
  transition: 0.1s ease all;
  background-color: ${({ _background }) => _background};
`;
